import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { maturacaoApi, MaturacaoItem } from '../api/api';

const DURACOES = [
  { dias: 7, label: '7 dias', hint: 'Mais rápido' },
  { dias: 14, label: '14 dias', hint: 'Recomendado' },
  { dias: 21, label: '21 dias', hint: 'Mais seguro' },
  { dias: 30, label: '30 dias', hint: 'Conservador' },
];

export default function MaturacaoPage() {
  const [itens, setItens] = useState<MaturacaoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  // Configurador de nova maturacao
  const [selecionados, setSelecionados] = useState<number[]>([]);
  const [duracao, setDuracao] = useState<number>(14);

  const carregar = useCallback(async () => {
    try {
      const { data } = await maturacaoApi.list();
      setItens(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar maturação');
      setItens([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
    const iv = setInterval(carregar, 15000);
    return () => clearInterval(iv);
  }, [carregar]);

  const disponiveis = useMemo(
    () => itens.filter(i => !i.maturacao || i.maturacao.status === 'concluida'),
    [itens],
  );

  const emAndamento = useMemo(
    () => itens.filter(i => i.maturacao && i.maturacao.status !== 'concluida'),
    [itens],
  );

  const toggleSel = (sessionId: number) => {
    setSelecionados(s => s.includes(sessionId) ? s.filter(x => x !== sessionId) : [...s, sessionId]);
  };

  const selecionarTodos = () => {
    const elegiveis = disponiveis.filter(i => i.sessionStatus === 'connected');
    setSelecionados(elegiveis.map(i => i.sessionId));
  };

  const iniciarLote = async () => {
    if (selecionados.length === 0) { setError('Selecione ao menos um número'); return; }
    setBusy(true);
    setError('');
    setMsg('');
    try {
      await maturacaoApi.iniciarLote(selecionados, duracao);
      setSelecionados([]);
      setMsg(`Maturação iniciada para ${selecionados.length} número(s) por ${duracao} dias.`);
      setTimeout(() => setMsg(''), 4000);
      await carregar();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao iniciar maturação');
    } finally {
      setBusy(false);
    }
  };

  const acaoSessao = async (sessionId: number, action: 'pausar' | 'retomar' | 'parar') => {
    setBusyId(sessionId);
    setError('');
    try {
      if (action === 'pausar') await maturacaoApi.pausar(sessionId);
      if (action === 'retomar') await maturacaoApi.retomar(sessionId);
      if (action === 'parar') await maturacaoApi.parar(sessionId);
      await carregar();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro na operação');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto w-full">
        <div className="flex items-center justify-center h-64 text-sm text-ink-secondary">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto w-full space-y-5">

      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-ink-primary tracking-tight">Maturação de Chips</h1>
        <p className="text-sm text-ink-secondary mt-1">
          Selecione os números, escolha por quantos dias quer aquecer e o sistema cuida do resto.
        </p>
      </div>

      {/* Toasts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-btn px-4 py-3 text-sm text-red-700">{error}</div>
      )}
      {msg && (
        <div className="rounded-btn px-4 py-3 text-sm" style={{ background: 'rgba(37, 99, 235, 0.18)', color: '#3B82F6' }}>
          {msg}
        </div>
      )}

      {/* Empty state — sem sessoes nenhuma */}
      {itens.length === 0 && (
        <div className="bg-white border border-line rounded-card px-6 py-16 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(37, 99, 235, 0.15)' }}>
            <svg className="w-8 h-8" fill="none" stroke="#3B82F6" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <p className="text-base font-semibold text-ink-primary">Nenhuma sessão para maturar</p>
          <p className="text-sm text-ink-secondary mt-1 max-w-sm">
            Conecte ao menos 2 números de WhatsApp em "Conectar WhatsApp" para começar o aquecimento.
          </p>
        </div>
      )}

      {/* CONFIGURADOR — nova maturação */}
      {itens.length > 0 && (
      <div className="bg-white border border-line rounded-card p-6 space-y-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-secondary">
          Iniciar nova maturação
        </p>

        {/* Duração */}
        <div>
          <p className="text-sm font-semibold text-ink-primary mb-2">1. Duração da maturação</p>
          <div className="flex flex-wrap gap-2">
            {DURACOES.map(d => {
              const ativo = duracao === d.dias;
              return (
                <button
                  key={d.dias}
                  type="button"
                  onClick={() => setDuracao(d.dias)}
                  className="rounded-btn border px-4 py-2.5 text-sm font-semibold transition-colors"
                  style={ativo
                    ? { background: '#3B82F6', borderColor: '#3B82F6', color: '#FFFFFF' }
                    : { background: '#FFFFFF', borderColor: '#E2E7F1', color: '#121217' }
                  }
                >
                  {d.label}
                  <span className={`block text-[10px] font-normal mt-0.5 ${ativo ? '' : 'text-ink-secondary'}`}>
                    {d.hint}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Seleção de sessões */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-ink-primary">
              2. Selecione os números ({selecionados.length} selecionado{selecionados.length !== 1 ? 's' : ''})
            </p>
            {disponiveis.some(i => i.sessionStatus === 'connected') && (
              <button
                type="button"
                onClick={selecionarTodos}
                className="text-xs font-semibold text-ink-secondary hover:text-ink-primary transition-colors"
              >
                Selecionar todos conectados
              </button>
            )}
          </div>

          {disponiveis.length === 0 ? (
            <div className="bg-surface-subtle border border-line rounded-btn p-5 text-center text-sm text-ink-secondary">
              Todas as suas sessões já estão em maturação. Veja abaixo o progresso.
            </div>
          ) : (
            <div className="space-y-2">
              {disponiveis.map(item => {
                const ativo = selecionados.includes(item.sessionId);
                const desabilitado = item.sessionStatus !== 'connected';
                return (
                  <label
                    key={item.sessionId}
                    className={`flex items-center gap-3 px-4 py-3 rounded-btn border cursor-pointer transition-colors ${
                      desabilitado ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    style={{
                      borderColor: ativo ? '#3B82F6' : '#E2E7F1',
                      background: ativo ? 'rgba(37, 99, 235, 0.1)' : '#FFFFFF',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={ativo}
                      disabled={desabilitado}
                      onChange={() => !desabilitado && toggleSel(item.sessionId)}
                      className="w-4 h-4 accent-blue-500 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-ink-primary truncate">{item.sessionNome}</p>
                      <p className="text-xs text-ink-secondary truncate">{item.sessionNumero || 'Sem número'}</p>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0 ${
                        item.sessionStatus === 'connected'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${item.sessionStatus === 'connected' ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                      {item.sessionStatus === 'connected' ? 'Conectada' : 'Desconectada'}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Botão iniciar */}
        <button
          type="button"
          onClick={iniciarLote}
          disabled={busy || selecionados.length === 0}
          className="w-full h-12 rounded-btn text-sm font-semibold hover:opacity-80 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
          style={{ background: '#3B82F6', color: '#FFFFFF' }}
        >
          {busy ? 'Iniciando...' : `Iniciar maturação ${selecionados.length > 0 ? `para ${selecionados.length} número${selecionados.length !== 1 ? 's' : ''} por ${duracao} dias` : ''}`}
        </button>
        <p className="text-xs text-ink-secondary text-center">
          Quando os {duracao} dias terminarem, a maturação para automaticamente.
        </p>
      </div>
      )}

      {/* MATURAÇÕES EM ANDAMENTO */}
      {emAndamento.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-secondary mb-3">
            Em andamento
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {emAndamento.map(item => {
              const m = item.maturacao!;
              const progressoTotal = Math.min(100, Math.round(((m.diaAtual - 1) / m.duracaoDias) * 100));

              return (
                <div key={item.sessionId} className="bg-white border border-line rounded-card p-5 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink-primary truncate">{item.sessionNome}</p>
                      <p className="text-xs text-ink-secondary truncate">{item.sessionNumero || 'Sem número'}</p>
                    </div>
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0"
                      style={m.status === 'ativa'
                        ? { background: '#3B82F6', color: '#FFFFFF' }
                        : { background: '#FEF3C7', color: '#92400E' }
                      }
                    >
                      {m.status === 'ativa' ? 'Aquecendo' : 'Pausada'}
                    </span>
                  </div>

                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-btn p-3 text-center" style={{ background: '#F8FAFC' }}>
                        <p className="text-xs text-ink-secondary">Dia atual</p>
                        <p className="text-lg font-bold text-ink-primary mt-0.5">{m.diaAtual} / {m.duracaoDias}</p>
                      </div>
                      <div className="rounded-btn p-3 text-center" style={{ background: '#F8FAFC' }}>
                        <p className="text-xs text-ink-secondary">Enviadas hoje</p>
                        <p className="text-lg font-bold text-ink-primary mt-0.5">{m.msgsHoje}</p>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="text-ink-secondary">Progresso total</span>
                        <span className="font-semibold text-ink-primary">{progressoTotal}%</span>
                      </div>
                      <div className="w-full bg-surface-subtle rounded-full h-2 overflow-hidden">
                        <div
                          className="h-2 rounded-full transition-all"
                          style={{ width: `${progressoTotal}%`, background: '#3B82F6' }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-1">
                    {m.status === 'ativa' && (
                      <button
                        disabled={busyId === item.sessionId}
                        onClick={() => acaoSessao(item.sessionId, 'pausar')}
                        className="px-3 py-1.5 rounded-btn text-xs font-semibold bg-surface-subtle text-ink-primary hover:opacity-80 disabled:opacity-50"
                      >
                        Pausar
                      </button>
                    )}
                    {m.status === 'pausada' && (
                      <button
                        disabled={busyId === item.sessionId}
                        onClick={() => acaoSessao(item.sessionId, 'retomar')}
                        className="px-3 py-1.5 rounded-btn text-xs font-semibold hover:opacity-80 disabled:opacity-50"
                        style={{ background: '#3B82F6', color: '#FFFFFF' }}
                      >
                        Retomar
                      </button>
                    )}
                    <button
                      disabled={busyId === item.sessionId}
                      onClick={() => acaoSessao(item.sessionId, 'parar')}
                      className="px-3 py-1.5 rounded-btn text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      Parar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
